import {
  DayMaskDefinition,
  EdgeInstanceType,
  EnabledDateRange,
  isDayMaskDefinition,
  isEdge,
  isNonNaNNumber,
  isUTCDayMaskPeriod,
  isUTCPeriodMins,
  UTCDayMaskPeriod,
  UTCPeriodHrMins,
  ValidBusinessObject,
} from '@iotv/iotv-v3-types'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../../../util/lambda/debug'
import { EntityProgramme } from './EntityProgramme'

/** CRA 2025 05 — TODO: this is bad technical debt, but I don't know how else to do it... */
export const commonSupportedTypes = ['Valve', 'WaterCannon'].sort((a, b) => a.localeCompare(b))

export const supportedTypes = Object.fromEntries(
  [...commonSupportedTypes]
    .filter((x) => x)
    .map((type) => [type, type])
    .sort(),
)

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && true)

export type IoTVEntitySequentialProgrammeDef = {
  enable: boolean
  scheduleNumber: number
} & UTCPeriodHrMins & {
    dayMask: DayMaskDefinition
    moistureThreshold: number
  }

export type IoTVEntitySequentialProgrammeEnabledDef = IoTVEntitySequentialProgrammeDef & {
  enable: true
}

export type IoTVEntitySequentialProgrammeDisabledDef = IoTVEntitySequentialProgrammeDef & {
  enable: false
}

export type EntityProgrammeConstructorProps = {
  /** the id of the programme which will be create if not present */
  id?: string
  /** the type id of the entity to be scheduled. As of 2024 12 one per scheduler, although this could be made mulitple with additional context queries */
  supportedType: string

  /** */
  enabled: boolean
  /** DayMask object if known */
  dayMask: DayMaskDefinition | null
  /** timeslots if already known */
  timeslots: UTCPeriodHrMins[] | null

  /** set the max number of entities per timeslot, if present. This is to allow enforcement of physical limitation eg Programme controls water system with limited pressure */
  maxNumberOfEntitiesPerTimeslot: number | null

  /** name of programme */
  name: string
}

export const isEntityProgramme = (x: any): x is EntityProgrammeConstructorProps => {
  const c = x as EntityProgrammeConstructorProps
  const isConstructorProps = isEntityProgrammeConstructorProps(x)
  const isInstance = c instanceof EntityProgramme
  const isEntityProgramme = isConstructorProps && isInstance
  if (c && !isEntityProgramme) {
    ;(c as EntityProgrammeConstructorProps & ValidBusinessObject)?.type === 'EntityProgramme' &&
      console.log(`WARN isEntityProgramme returning false for EntityProgramme ${c.id} with`, { isConstructorProps, isInstance })
  }
  return isEntityProgramme
}

export const isEntityProgrammeConstructorProps = (x: any): x is EntityProgrammeConstructorProps => {
  const { dayMask, maxNumberOfEntitiesPerTimeslot, name, timeslots } = (x as EntityProgrammeConstructorProps) ?? {}
  const isEntityProgrammeConstructorProps =
    (!maxNumberOfEntitiesPerTimeslot || isNonNaNNumber(maxNumberOfEntitiesPerTimeslot) || maxNumberOfEntitiesPerTimeslot === null) &&
    typeof name === 'string' &&
    (dayMask === null || isDayMaskDefinition(dayMask)) &&
    (timeslots === null || (timeslots instanceof Array && (timeslots.length === 0 || timeslots.every(isUTCPeriodMins))))
  if (!isEntityProgrammeConstructorProps) {
    ;(x as EntityProgrammeConstructorProps & ValidBusinessObject)?.type === 'EntityProgramme' &&
      console.log(`WARN isEntityProgrammeConstructorProps returning false  for EntityProgramme ${x?.id} with`, {
        maxNumberOfEntitiesPerTimeslot,
        name,
        dayMask,
        timeslots,
      })
  }

  return isEntityProgrammeConstructorProps
}

export type MutableEntityProgrammeAtts = Pick<EntityProgrammeConstructorProps, 'enabled'>

export interface CommonFunctions<T extends IEntityProgramme> {
  getTimeslotsLength(): number
  pushTimeslot(timeslot: UTCPeriodHrMins): T
  popTimeslot(): UTCPeriodHrMins | undefined
  isValid(): boolean
}

export interface IEntityProgrammeFunctions {
  /** return a clone of the programme, ensuring timeslots and dayMask are deep cloned */
  clone(): EntityProgramme

  /** helper function to return the programmes slots as chart compatible date ranges */
  timeslotsToDateRanges: ({ relativeDate }: { relativeDate: Date }) => EnabledDateRange[]
}

export interface IEntityProgramme extends CommonFunctions<IEntityProgramme>, IEntityProgrammeFunctions, ValidBusinessObject {
  /** the type id of the entity to be scheduled. As of 2024 12 one per scheduler, although this could be made mulitple with additional context queries */
  supportedType: string
  /** whether this programme is enabled */
  enabled: boolean

  maxNumberOfEntitiesPerTimeslot: number | null
  /** name of programme */
  name: string
  /** */
  dayMask: DayMaskDefinition
  /**  */
  timeslots: UTCPeriodHrMins[]
}

export type ProgrammeToItemEdgeTypeAttributes = {
  schedules: UTCDayMaskPeriod[]
}

export const isProgrammeToItemEdgeType = (x: any): x is ProgrammeToItemEdgeInstance => {
  const c = x as ProgrammeToItemEdgeInstance
  return !!c && isEdge(c) && !!c.edgeAttributes && c.edgeAttributes.schedules && c.edgeAttributes.schedules.every(isUTCDayMaskPeriod)
}

export type ProgrammeToItemEdgeInstance = EdgeInstanceType<string, ProgrammeToItemEdgeTypeAttributes> & { edgeAttributes: ProgrammeToItemEdgeTypeAttributes }

export type EntityProgrammeSetConstructorProps = {
  /** the id of the programme which will be create if not present */
  id?: string
  /** the type id of the entity to be scheduled by linked programmed.  */
  supportedType: string
  /** the maximum number of schedules allowed to be generated for a schedulable item */
  maxProgrammes: number | null
  /** name of programme */
  name: string
  /** flag the ProgrammeSet as enabled or disabled */
  enabled: boolean
  /** list of time at which the programme set has been applieed */
  appliedTimestampsMs: number[] | null
}

export const isEntityProgrammeSetConstructorProps = (x: any): x is EntityProgrammeSetConstructorProps => {
  const c = (x as EntityProgrammeSetConstructorProps) ?? {}
  const { maxProgrammes, enabled, supportedType, name, appliedTimestampsMs } = c
  return (
    /*typeof enabled === 'boolean' && typeof supportedType === 'string' &&*/ typeof name === 'string' &&
    (maxProgrammes === null || typeof maxProgrammes === 'number') &&
    (!appliedTimestampsMs || (appliedTimestampsMs instanceof Array && appliedTimestampsMs.every(isNonNaNNumber)))
  )
}
